import * as React from 'react';

import {AccountAvatar} from 'shells/avatar/account-avatar';
import {Button} from 'shells/button/button';
import {TrashRegularIcon} from 'shells/icon/svg/trash-regular-icon';
import {StackLegacy} from 'shells/layout/stack/stack-legacy';
import {AccountCard} from 'shells/peep-cards/account-card/account-card';
import {Popover} from 'shells/popover/popover';

import type {ParentChildFragment} from 'nutshell-graphql-types';

import {AllChildAccounts} from './all-child-accounts';

const VIEW_ALL_CHILD_ACCOUNTS_THRESHOLD = 3;

type Props = {
    parentAccountId: string;
    childAccounts: ParentChildFragment[];
    onRemoveParent: (id: string) => void;
    isAddingChild: boolean;
};

export function ChildAccounts(props: Props) {
    const [isShowingPopover, setIsShowingPopover] = React.useState<boolean>(false);
    const popoverAnchorRef = React.useRef(null);

    const firstThreeChildAccounts = props.childAccounts.slice(0, VIEW_ALL_CHILD_ACCOUNTS_THRESHOLD);
    const shouldShowViewAllButton =
        !props.isAddingChild && props.childAccounts.length > VIEW_ALL_CHILD_ACCOUNTS_THRESHOLD;

    React.useEffect(() => {
        // Handles instances where a user removes the fourth child account while the popover is open
        if (isShowingPopover && props.childAccounts.length <= VIEW_ALL_CHILD_ACCOUNTS_THRESHOLD) {
            setIsShowingPopover(false);
        }
    }, [props.childAccounts, isShowingPopover]);

    return (
        <>
            <StackLegacy spacing={8}>
                {firstThreeChildAccounts.map((child) => (
                    <AccountCard
                        key={child.id}
                        name={child.name}
                        icon={<AccountAvatar avatarUrl={child.avatarUrl} />}
                        accountUrl={child.htmlUrlPath}
                        description={child.description}
                        location={child.primaryAddressRegion}
                        toolbarProps={{
                            options: [
                                {
                                    id: 'remove-association',
                                    tooltipText: 'Remove association',
                                    icon: TrashRegularIcon,
                                    onClick: () => props.onRemoveParent(child.id),
                                },
                            ],
                        }}
                    />
                ))}
                {shouldShowViewAllButton ? (
                    <Button
                        variant='text-primary'
                        noPadding={true}
                        onClick={() => setIsShowingPopover(true)}
                        getButtonRef={popoverAnchorRef}
                    >
                        View all {props.childAccounts.length} child companies
                    </Button>
                ) : undefined}
            </StackLegacy>
            {isShowingPopover ? (
                <Popover
                    preventBodyScroll={true}
                    onBlur={() => setIsShowingPopover(false)}
                    location='left'
                    anchor={popoverAnchorRef.current}
                >
                    <AllChildAccounts
                        parentAccountId={props.parentAccountId}
                        childAccounts={props.childAccounts}
                        onRemoveParent={(childId: string) => props.onRemoveParent(childId)}
                    />
                </Popover>
            ) : undefined}
        </>
    );
}
